/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { Article, Archetype, YouTubeVideo } from "./types";

// Images generated via AI Studio tools - Logo updated with direct pristine asset
export const BRAND_IMAGES = {
  heroBg: "/images/persepolis_great_hall_bg_1782241508762.avif",
  brandSeal: "/logo@2x.avif",
  headerBannerBg: "/images/achaemenid_wall_banner_1782388330025.avif",
};

export const ARCHE_TYPES: Archetype[] = [
  {
    id: "shadow",
    name: "سایه",
    englishName: "The Shadow",
    symbol: "دو مار ضحاک",
    symbolEn: "Zahhak's Two Shoulders' Snakes",
    description: "بخش‌های سرکوب‌شده، پنهان، ناخوشایند و ناشناخته شخصیت که آگاهی تمایلی به پذیرش آنها ندارد، اما منبع عظیم خلاقیت و نیروی حیاتی نیز هستند.",
    descriptionEn: "The repressed, hidden, unpleasant, and unknown aspects of the personality that consciousness refuses to acknowledge, which are also an immense source of creativity and vital life force.",
    jungianConcept: "بخش تاریک و حیوانی روان که برای رسیدن به یکپارچگی باید آن را شناخت و مهار کرد، نه اینکه انکار نمود.",
    jungianConceptEn: "The dark and animalistic part of the psyche that must be recognized and integrated to achieve wholeness, rather than denied.",
    persianEquivalent: "دیو درون، نفس اماره، اژدها یا ضحاک ماردوش در حماسه ملی.",
    persianEquivalentEn: "The inner demon, ego-driven desires (Nafs-e Ammarah), or the dragon/Zahhak with shoulder-snakes in national epic."
  },
  {
    id: "self",
    name: "خودِ برتر",
    englishName: "The Self",
    symbol: "سیمرغ / فروهر",
    symbolEn: "Simurgh / Faravahar",
    description: "نقطه مرکزی و تمامیت روان، نشان‌دهنده آشتی اضداد، صلح درونی و هماهنگی کامل میان بخش‌های خودآگاه و ناخودآگاه روان.",
    descriptionEn: "The central point and totality of the psyche, representing the reconciliation of opposites, inner peace, and perfect harmony between consciousness and the unconscious.",
    jungianConcept: "هدف نهایی فرآیند تفرد که فراتر از منیت (Ego) شخصی است و مرکز معنوی و وجودی انسان را تشکیل می‌دهد.",
    jungianConceptEn: "The ultimate goal of the individuation process, transcending the personal Ego, and forming the spiritual and existential center of human being.",
    persianEquivalent: "سیمرغ در منطق‌الطیر عطار، فر کیانی (فروهر) یا انسان کامل در عرفان.",
    persianEquivalentEn: "Simurgh in Attar's Conference of the Birds, Royal Aura (Farr-e Kiyani), or the Perfect Human in mysticism."
  },
  {
    id: "anima_animus",
    name: "آنیما و آنیموس",
    englishName: "Anima & Animus",
    symbol: "پیوند زال و رودابه",
    symbolEn: "Union of Zal and Rudabeh",
    description: "جنبه زنانه ناخودآگاه مرد (آنیما) و جنبه مردانه ناخودآگاه زن (آنیموس) که واسطه‌های حیاتی میان من آگاه و دنیای پررمز و راز درونی هستند.",
    descriptionEn: "The feminine aspect of a man's unconscious (Anima) and the masculine aspect of a woman's unconscious (Animus), serving as vital mediators between the conscious ego and the inner world of mystery.",
    jungianConcept: "عنصر مادینه روان که نماد الهام، احساس و خلاقیت است و عنصر نرینه روان که نماد منطق، اراده و ابراز وجود است.",
    jungianConceptEn: "The feminine element of the psyche symbolizing inspiration, emotion, and creativity; and the masculine element symbolizing logic, will, and self-assertion.",
    persianEquivalent: "ملاقات قهرمان با سیمرغ، پیوند زال با رودابه، یا تهمینه و سودابه.",
    persianEquivalentEn: "The hero's encounter with Simurgh, the marriage of Zal and Rudabeh, or the figures of Tahmineh and Sudabeh."
  },
  {
    id: "persona",
    name: "نقاب",
    englishName: "The Persona",
    symbol: "کلاه‌خود و زره رستم",
    symbolEn: "Rostam's Helmet and Armor",
    description: "چهره اجتماعی و ماسکی که انسان در مواجهه با جامعه بر چهره می‌گذارد تا مورد پذیرش قرار گیرد و انتظارات دنیای بیرونی را برآورده کند.",
    descriptionEn: "The social face and mask an individual wears when facing society to be accepted and to meet the expectations of the external world.",
    jungianConcept: "سازش میان فرد و جامعه؛ نقاب برای زندگی اجتماعی لازم است اما یکی شدن با آن باعث فراموشی خود واقعی می‌شود.",
    jungianConceptEn: "A compromise between the individual and society; the Persona is necessary for social life, but identifying too deeply with it leads to forgetting the true self.",
    persianEquivalent: "خرقه صوفی، جامه جنگ‌آوران یا القاب رسمی هخامنشی.",
    persianEquivalentEn: "The Sufi cloak, warrior armor, or formal Achaemenid royal titles."
  },
  {
    id: "wise_old_man",
    name: "پیر فرزانه",
    englishName: "The Wise Old Man",
    symbol: "زردشت / جام جم",
    symbolEn: "Zoroaster / Cup of Jamshid",
    description: "کهن‌الگوی راهنمایی، حکمت باستانی، تدبیر غریزی و نجات در لحظات استیصال که وقتی آگاهی به بن‌بست می‌رسد، ظهور می‌کند.",
    descriptionEn: "The archetype of guidance, ancient wisdom, instinctive foresight, and rescue in moments of despair, emerging when consciousness reaches a dead end.",
    jungianConcept: "تجسم خرد جمعی و بینش ناخودآگاه که قهرمان را در سفر قهرمانی هدایت می‌کند.",
    jungianConceptEn: "The embodiment of collective wisdom and unconscious insights guiding the hero on their journey.",
    persianEquivalent: "سیمرغ، زال، جام جهان‌نما، یا پیر مغان در ادبیات حافظ.",
    persianEquivalentEn: "Simurgh, Zal, the Cup of Jamshid, or the Wise Elder of the Magi in Hafez's poetry."
  },
  {
    id: "mother",
    name: "کهن‌الگوی مادر",
    englishName: "The Mother Archetype",
    symbol: "رحم طبیعت / تهمینه یا رودابه",
    symbolEn: "Womb of Nature / Tahmineh or Rudabeh",
    description: "مظهر مراقبت، تغذیه، پناه، پرورش و منبع حیات‌بخش روان؛ اما در عین حال دارای جنبه‌ای بلعنده و نگهدارنده که مانع رشد مستقل فرزند می‌شود.",
    descriptionEn: "The embodiment of care, nourishment, sanctuary, and life-giving force of the psyche; yet retaining a devouring and holding aspect that can hinder independent growth.",
    jungianConcept: "نماد ناخودآگاه خلاق و پرورش‌دهنده که مهد تولد روان جدید است و در دو جنبه مادر نیکوکار و مادر مخرب متجلی می‌شود.",
    jungianConceptEn: "The symbol of the creative and nourishing unconscious, the cradle of the emerging psyche, manifested in both benevolent and destructive (devouring) aspects.",
    persianEquivalent: "آناهیتا (ایزدبانوی آب‌ها)، رودابه، تهمینه، یا سپندارمد (زمین بارور).",
    persianEquivalentEn: "Anahita (Goddess of Waters), Rudabeh, Tahmineh, or Spandarmad (the Fertile Earth)."
  },
  {
    id: "hero",
    name: "کهن‌الگوی قهرمان",
    englishName: "The Hero Archetype",
    symbol: "سفر رستم / هفت‌خوان",
    symbolEn: "Rostam's Journey / Seven Labors",
    description: "نیروی محرکه روان برای غلبه بر تاریکی، رویارویی با موانع درون و بیرون، و دست‌یابی به استقلال شخصیت و آگاهی فردی در سفر تکامل معنوی.",
    descriptionEn: "The driving force of the psyche to overcome darkness, confront internal and external obstacles, and achieve ego independence and individual consciousness on the spiritual journey.",
    jungianConcept: "مرحله‌ای حیاتی در فرآیند تفرد که در آن فرد باید از وابستگی به مادر/ناخودآگاه رها شده و با غلبه بر هیولاها، آگاهی مجزای خود را اثبات کند.",
    jungianConceptEn: "A crucial stage in the individuation process where the individual must break free from dependence on the mother/unconscious, conquering monsters to assert distinct consciousness.",
    persianEquivalent: "رستم دستان در هفت‌خوان، فریدون بر ضد ضحاک، یا گردآفرید مبارز در شاهنامه.",
    persianEquivalentEn: "Rostam of Zabulistan in his Seven Labors, Fereydun against Zahhak, or the fierce warrior maiden Gordafarid in Shahnameh."
  }
];

export const ARTICLES: Article[] = [
  {
    id: "33",
    slug: "xerxes-whipping-the-sea",
    title: "خشایارشا و شلاق دریا: تورم من در دل تاریخ مستند",
    titleEn: "Xerxes and Whipping the Sea: Ego Inflation in Documented History",
    brief: "روایت تاریخی شلاق زدن دریا به فرمان خشایارشا در تنگه هلسپونت و تحلیل روان‌شناختی آن از منظر مفهوم یونگی تورم من.",
    briefEn: "A historical narrative of Xerxes whipping the sea at the Hellespont and its psychological analysis through the lens of Jungian Ego Inflation.",
    readTime: "۶ دقیقه",
    readTimeEn: "6 min read",
    category: "ناخودآگاه و سایه",
    categoryEn: "Unconscious & Shadow",
    archetype: "shadow",
    mdPath: "/articles/xerxes-whipping-the-sea.md",
    coverImage: "https://www.razoravan.linkpc.net/images/xerxes-whipping-the-sea.avif",
    publishDate: "۱۴۰۵/۰۴/۲۰",
    publishDateEn: "July 10, 2026",
    tags: ["خشایارشا", "سایه", "تورم من", "هخامنشی", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Xerxes", "Shadow", "Ego Inflation", "Achaemenid", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "32",
    slug: "manijeh-fearless-loyalty",
    title: "منیژه: وفاداری‌ای که از زندان نترسید",
    titleEn: "Manijeh: A Loyalty That Feared No Prison",
    brief: "تحلیل شخصیت منیژه به عنوان نماد وفاداری عاشقانه آگاهانه و مقاومت در برابر پدری خودکامه با از دست دادن تمام امتیازات سلطنتی.",
    briefEn: "An archetypal study of Manijeh as a symbol of active loyalty, sacrifice, and psychological resistance against tyrannical patriarchal authority.",
    readTime: "۵ دقیقه",
    readTimeEn: "5 min read",
    category: "روان‌کاوی کهن‌الگویی",
    categoryEn: "Archetypal Psychoanalysis",
    archetype: "anima_animus",
    mdPath: "/articles/manijeh-fearless-loyalty.md",
    coverImage: "https://www.razoravan.linkpc.net/images/manijeh-fearless-loyalty.avif",
    publishDate: "۱۴۰۵/۰۴/۲۰",
    publishDateEn: "July 10, 2026",
    tags: ["منیژه", "وفاداری", "آنیما", "زن", "روانشناسی", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Manijeh", "Loyalty", "Anima", "Woman", "Psychology", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "31",
    slug: "rudabeh-love-across-enemy-lines",
    title: "رودابه: عشقی که مرز دو دشمن را شکست",
    titleEn: "Rudabeh: The Love That Broke the Borders of Two Enemies",
    brief: "بررسی کهن‌الگوی آنیمای فعال در داستان رودابه و زال؛ زنی که آگاهانه و مستقل مرزهای سیاسی و خانوادگی دشمنان را به چالش کشید.",
    briefEn: "An analysis of the active Anima archetype in the story of Rudabeh and Zal, exploring how conscious love can challenge familial and political barriers.",
    readTime: "۵ دقیقه",
    readTimeEn: "5 min read",
    category: "روان‌کاوی کهن‌الگویی",
    categoryEn: "Archetypal Psychoanalysis",
    archetype: "anima_animus",
    mdPath: "/articles/rudabeh-love-across-enemy-lines.md",
    coverImage: "https://www.razoravan.linkpc.net/images/rudabeh-love-across-enemy-lines.avif",
    publishDate: "۱۴۰۵/۰۴/۲۰",
    publishDateEn: "July 10, 2026",
    tags: ["رودابه", "زال", "آنیما", "زن", "روانشناسی", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Rudabeh", "Zal", "Anima", "Woman", "Psychology", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "30",
    slug: "cambyses-madness-projection",
    title: "کمبوجیه: پادشاهی که به جنون متهم شد",
    titleEn: "Cambyses: The King Accused of Madness",
    brief: "بررسی روان‌شناختی اتهام جنون به کمبوجیه، دومین پادشاه هخامنشی، از منظر هرودوت و نظریه فرافکنی سایه در سطح تاریخ‌نگاری جمعی.",
    briefEn: "A psychological examination of the accusation of madness against Cambyses II, through the lens of collective shadow projection in historical narrative.",
    readTime: "۶ دقیقه",
    readTimeEn: "6 min read",
    category: "ناخودآگاه و سایه",
    categoryEn: "Unconscious & Shadow",
    archetype: "shadow",
    mdPath: "/articles/cambyses-madness-projection.md",
    coverImage: "https://www.razoravan.linkpc.net/images/cambyses-madness-projection.avif",
    publishDate: "۱۴۰۵/۰۴/۱۸",
    publishDateEn: "July 8, 2026",
    tags: ["کمبوجیه", "جنون", "سایه", "فرافکنی", "هخامنشی", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Cambyses", "Madness", "Shadow", "Projection", "Achaemenid", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "29",
    slug: "layla-majnun-anima",
    title: "لیلی و مجنون: فرافکنی آنیما بر معشوقی که هرگز در دسترس نبود",
    titleEn: "Layla and Majnun: Anima Projection on the Elusive Beloved",
    brief: "تحلیل غزل‌واره‌ی لیلی و مجنون نظامی گنجوی بر اساس کهن‌الگوی آنیما و خطرات فرافکنی افراطی تصویر درونی بر معشوق بیرونی.",
    briefEn: "An analysis of Nizami's Layla and Majnun through the lens of the Anima archetype, exploring the dangers of extreme inner projection on an external partner.",
    readTime: "۶ دقیقه",
    readTimeEn: "6 min read",
    category: "روان‌کاوی کهن‌الگویی",
    categoryEn: "Archetypal Psychoanalysis",
    archetype: "anima_animus",
    mdPath: "/articles/layla-majnun-anima.md",
    coverImage: "https://www.razoravan.linkpc.net/images/layla-majnun-anima.avif",
    publishDate: "۱۴۰۵/۰۴/۱۸",
    publishDateEn: "July 8, 2026",
    tags: ["لیلی", "مجنون", "آنیما", "فرافکنی", "نظامی گنجوی", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Layla", "Majnun", "Anima", "Projection", "Nizami", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "28",
    slug: "bahram-gur-haft-paykar",
    title: "بهرام گور و هفت پیکر: هفت رنگ روان در مسیر کمال",
    titleEn: "Bahram Gur and Haft Paykar: Seven Colors of the Soul on the Path to Perfection",
    brief: "بررسی سفر بهرام گور در هفت کاخ هفت رنگ نظامی گنجوی به عنوان نقشه‌ی راه نمادین فرآیند فردیت و یکپارچگی روانی یونگی.",
    briefEn: "Exploring Bahram Gur's journey through Nizami's seven colored palaces as a symbolic map of the Jungian individuation process and psychic integration.",
    readTime: "۷ دقیقه",
    readTimeEn: "7 min read",
    category: "تحلیل اساطیر",
    categoryEn: "Mythological Analysis",
    archetype: "self",
    mdPath: "/articles/bahram-gur-haft-paykar.md",
    coverImage: "https://www.razoravan.linkpc.net/images/bahram-gur-haft-paykar.avif",
    publishDate: "۱۴۰۵/۰۴/۱۸",
    publishDateEn: "July 8, 2026",
    tags: ["بهرام گور", "هفت پیکر", "تفرد", "نظامی گنجوی", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Bahram Gur", "Haft Paykar", "Individuation", "Nizami", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "27",
    slug: "ferdowsi-collective-guardian",
    title: "فردوسی: کهن‌الگوی خالق و نگهبان هویت جمعی",
    titleEn: "Ferdowsi: The Creator Archetype and Guardian of Collective Identity",
    brief: "تحلیل شخصیت حکیم ابوالقاسم فردوسی به عنوان تجسم کهن‌الگوی نگهبان ناخودآگاه جمعی و حافظ اسطوره‌ها و زبان یک ملت.",
    briefEn: "Analyzing Ferdowsi as the embodiment of the Guardian of collective identity archetype, preserving the myths and language of a nation.",
    readTime: "۷ دقیقه",
    readTimeEn: "7 min read",
    category: "روان‌کاوی کهن‌الگویی",
    categoryEn: "Archetypal Psychoanalysis",
    archetype: "wise_old_man",
    mdPath: "/articles/ferdowsi-collective-guardian.md",
    coverImage: "https://www.razoravan.linkpc.net/images/ferdowsi-collective-guardian.avif",
    publishDate: "۱۴۰۵/۰۴/۱۸",
    publishDateEn: "July 8, 2026",
    tags: ["فردوسی", "پیر فرزانه", "هویت جمعی", "شاهنامه", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Ferdowsi", "Wise Old Man", "Collective Identity", "Shahnameh", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "26",
    slug: "khayyam-death-acceptance",
    title: "خیام و رویارویی با مرگ: کهن‌الگوی پذیرش فناپذیری",
    titleEn: "Khayyam and the Confrontation with Death: The Archetype of Embracing Mortality",
    brief: "تحلیل رباعیات عمر خیام نیشابوری از منظر رویارویی با سایه‌ی مرگ و اضطراب وجودی، و روان‌شناسی پذیرش فناپذیری در نیمه‌ی دوم عمر.",
    briefEn: "Analyzing Omar Khayyam's Rubaiyat from the perspective of confronting the shadow of death, existential anxiety, and embracing mortality in the second half of life.",
    readTime: "۶ دقیقه",
    readTimeEn: "6 min read",
    category: "روان‌کاوی کهن‌الگویی",
    categoryEn: "Archetypal Psychoanalysis",
    archetype: "wise_old_man",
    mdPath: "/articles/khayyam-death-acceptance.md",
    coverImage: "https://www.razoravan.linkpc.net/images/khayyam-death-acceptance.avif",
    publishDate: "۱۴۰۵/۰۴/۱۸",
    publishDateEn: "July 8, 2026",
    tags: ["خیام", "مرگ", "پیر فرزانه", "رباعیات", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Khayyam", "Death", "Wise Old Man", "Rubaiyat", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "25",
    slug: "attar-simorgh-individuation",
    title: "منطق‌الطیر عطار: سی مرغ در جست‌وجوی خویشتن",
    titleEn: "Attar's Conference of the Birds: Thirty Birds in Search of the Self",
    brief: "تحلیل منطق‌الطیر عطار از منظر روان‌شناسی تحلیلی یونگ و پیوند تمثیل سی‌مرغ با فرآیند فردیت و دست‌یابی به خویشتنِ حقیقی.",
    briefEn: "An analysis of Attar's Conference of the Birds through Jungian psychology, linking the allegory of the thirty birds to the individuation process.",
    readTime: "۷ دقیقه",
    readTimeEn: "7 min read",
    category: "روان‌کاوی کهن‌الگویی",
    categoryEn: "Archetypal Psychoanalysis",
    archetype: "self",
    mdPath: "/articles/attar-simorgh-individuation.md",
    coverImage: "https://www.razoravan.linkpc.net/images/attar-simorgh-individuation.avif",
    publishDate: "۱۴۰۵/۰۴/۱۷",
    publishDateEn: "July 7, 2026",
    tags: ["عطار", "سیمرغ", "تفرد", "منطق‌الطیر", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Attar", "Simurgh", "Individuation", "Conference of the Birds", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "24",
    slug: "rumi-ney-nameh-separation",
    title: "نی‌نامه‌ی مولانا: فریاد جدایی از خویشتنِ اصیل",
    titleEn: "Rumi's Ney-Nameh: The Cry of Separation from the Authentic Self",
    brief: "بازخوانی ابیات آغازین مثنوی معنوی از منظر تمثیل نی و بستر روان‌شناختی اشتیاق برای بازگشت به یکپارچگی و هماهنگی روان.",
    briefEn: "Reinterpreting the opening verses of Rumi's Mathnawi through the reed metaphor and the psychological longing to return to psychic wholeness.",
    readTime: "۶ دقیقه",
    readTimeEn: "6 min read",
    category: "روان‌کاوی کهن‌الگویی",
    categoryEn: "Archetypal Psychoanalysis",
    archetype: "self",
    mdPath: "/articles/rumi-ney-nameh-separation.md",
    coverImage: "https://www.razoravan.linkpc.net/images/rumi-ney-nameh-separation.avif",
    publishDate: "۱۴۰۵/۰۴/۱۷",
    publishDateEn: "July 7, 2026",
    tags: ["مولانا", "نی‌نامه", "تفرد", "مثنوی", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Rumi", "Ney-Nameh", "Individuation", "Mathnawi", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "23",
    slug: "hafez-rend-trickster",
    title: "حافظ و کهن‌الگوی رند: خردی که در لباس شوخی پنهان شد",
    titleEn: "Hafez and the Rend Archetype: Wisdom Masked in Jest",
    brief: "بررسی شخصیت رند در غزل‌های حافظ به‌عنوان تجلی کهن‌الگوی حیله‌گر (تریکستر) و کارکرد آن در نقد و به چالش کشیدن نقاب اجتماعی یا پرسونا.",
    briefEn: "An exploration of the Rend in Hafez's poetry as a manifestation of the Trickster archetype, challenging the rigid social Persona.",
    readTime: "۶ دقیقه",
    readTimeEn: "6 min read",
    category: "نقاب اجتماعی",
    categoryEn: "Social Persona",
    archetype: "persona",
    mdPath: "/articles/hafez-rend-trickster.md",
    coverImage: "https://www.razoravan.linkpc.net/images/hafez-rend-trickster.avif",
    publishDate: "۱۴۰۵/۰۴/۱۷",
    publishDateEn: "July 7, 2026",
    tags: ["حافظ", "رند", "نقاب", "تریکستر", "غزل", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Hafez", "Rend", "Persona", "Trickster", "Ghazal", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "22",
    slug: "saadi-bani-adam-collective-unconscious",
    title: "سعدی و «بنی‌آدم»: کشف ناخودآگاه جمعی هفتصد سال پیش از یونگ",
    titleEn: "Saadi and \"Bani Adam\": Discovering the Collective Unconscious Seven Centuries Before Jung",
    brief: "تحلیل معروف‌ترین شعر بنی‌آدم سعدی بر اساس مفهوم ناخودآگاه جمعی یونگ و همدلی وجودی و غیرارادی در ژرفای روان بشری.",
    briefEn: "Analyzing Saadi's famous \"Bani Adam\" poem through the lens of Jung's Collective Unconscious and inherent existential empathy.",
    readTime: "۶ دقیقه",
    readTimeEn: "6 min read",
    category: "ناخودآگاه و سایه",
    categoryEn: "Unconscious & Shadow",
    archetype: "self",
    mdPath: "/articles/saadi-bani-adam-collective-unconscious.md",
    coverImage: "https://www.razoravan.linkpc.net/images/saadi-bani-adam-collective-unconscious.avif",
    publishDate: "۱۴۰۵/۰۴/۱۷",
    publishDateEn: "July 7, 2026",
    tags: ["سعدی", "بنی‌آدم", "ناخودآگاه جمعی", "همدلی", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Saadi", "Bani Adam", "Collective Unconscious", "Empathy", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "21",
    slug: "amirkabir-shadow-projection",
    title: "امیرکبیر: قربانی سایه‌ی حسادت درباری",
    titleEn: "Amir Kabir: Victim of the Courtly Shadow of Envy",
    brief: "تحلیل تاریخی و روان‌شناختی سقوط و قتل صدراعظم مصلح ایران، امیرکبیر، بر اساس کهن‌الگو و مکانیسم دفاعی فرافکنی سایه جمعی.",
    briefEn: "A historical and psychological analysis of the fall and murder of Amir Kabir through the lens of collective shadow projection.",
    readTime: "۷ دقیقه",
    readTimeEn: "7 min read",
    category: "ناخودآگاه و سایه",
    categoryEn: "Unconscious & Shadow",
    archetype: "shadow",
    mdPath: "/articles/amirkabir-shadow-projection.md",
    coverImage: "https://www.razoravan.linkpc.net/images/amirkabir-shadow-projection.avif",
    publishDate: "۱۴۰۵/۰۴/۱۷",
    publishDateEn: "July 7, 2026",
    tags: ["امیرکبیر", "سایه", "فرافکنی", "تاریخ", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Amir Kabir", "Shadow", "Projection", "History", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "20",
    slug: "ahriman-cosmic-shadow",
    title: "اهریمن: سایه‌ای که از سطح فردی به مقیاس کیهانی رسید",
    titleEn: "Ahriman: The Shadow That Transcended the Personal to a Cosmic Scale",
    brief: "بررسی مفهوم اهریمن در اندیشه زرتشتی به عنوان مظهر سایه کیهانی و مقایسه آن با سطوح سایه فردی و جمعی از منظر یونگ.",
    briefEn: "An analysis of Ahriman in Zoroastrian thought as the cosmic shadow, comparing it to personal and collective shadows from a Jungian perspective.",
    readTime: "۷ دقیقه",
    readTimeEn: "7 min read",
    category: "تحلیل اساطیر",
    categoryEn: "Mythological Analysis",
    archetype: "shadow",
    mdPath: "/articles/ahriman-cosmic-shadow.md",
    coverImage: "https://www.razoravan.linkpc.net/images/ahriman-cosmic-shadow.avif",
    publishDate: "۱۴۰۵/۰۴/۱۴",
    publishDateEn: "July 4, 2026",
    tags: ["اهریمن", "سایه", "زرتشت", "کیهان", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Ahriman", "Shadow", "Zoroaster", "Cosmic", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "19",
    slug: "zartosht-wise-old-man",
    title: "زرتشت: پیر فرزانه‌ای که راه را نه با شمشیر، که با پرسش گشود",
    titleEn: "Zoroaster: The Wise Old Man Who Opened the Path with Questions, Not Swords",
    brief: "تحلیل روان‌شناختی زرتشت به عنوان مظهر پویای کهن‌الگوی پیر فرزانه و نقش پرسشگری آگاهانه در گذار به فرآیند فردیت.",
    briefEn: "A psychological analysis of Zoroaster as a dynamic manifestation of the Wise Old Man archetype, exploring the role of conscious questioning.",
    readTime: "۷ دقیقه",
    readTimeEn: "7 min read",
    category: "روان‌کاوی کهن‌الگویی",
    categoryEn: "Archetypal Psychoanalysis",
    archetype: "wise_old_man",
    mdPath: "/articles/zartosht-wise-old-man.md",
    coverImage: "https://www.razoravan.linkpc.net/images/zartosht-wise-old-man.avif",
    publishDate: "۱۴۰۵/۰۴/۱۴",
    publishDateEn: "July 4, 2026",
    tags: ["زرتشت", "پیر فرزانه", "پرسش", "تفرد", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Zoroaster", "Wise Old Man", "Questioning", "Individuation", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "18",
    slug: "sohrab-puer-aeternus",
    title: "سهراب: پوئر اترنوس، یا فرزندی که هرگز فرصت بلوغ نیافت",
    titleEn: "Sohrab: Puer Aeternus, or the Child Who Never Had the Chance to Mature",
    brief: "بازخوانی داستان سهراب از منظر کهن‌الگوی فرزند ابدی (Puer Aeternus) و چالش‌های جستجوی هویت مستقل در سایه نام و نشان رستم.",
    briefEn: "Reinterpreting Sohrab through the lens of the Eternal Boy archetype (Puer Aeternus) and the quest for an independent identity.",
    readTime: "۷ دقیقه",
    readTimeEn: "7 min read",
    category: "روان‌کاوی کهن‌الگویی",
    categoryEn: "Archetypal Psychoanalysis",
    archetype: "shadow",
    mdPath: "/articles/sohrab-puer-aeternus.md",
    coverImage: "https://www.razoravan.linkpc.net/images/sohrab-puer-aeternus.avif",
    publishDate: "۱۴۰۵/۰۴/۱۴",
    publishDateEn: "July 4, 2026",
    tags: ["رستم", "سهراب", "عقده پدر", "پوئر اترنوس", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Rostam", "Sohrab", "Father Complex", "Puer Aeternus", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "17",
    slug: "fereydun-shadow-integration",
    title: "فریدون: قهرمانی که سایه را یکپارچه کرد، نه فقط نابود",
    titleEn: "Fereydun: The Hero Who Integrated the Shadow, Rather Than Simply Destroying It",
    brief: "تحلیل روان‌شناختی مهار و به بند کشیدن ضحاک در دماوند توسط فریدون به عنوان نمادی غنی از یکپارچه‌سازی و مدیریت فعال سایه درون.",
    briefEn: "A psychological analysis of Fereydun shackling Zahhak in Damavand as a rich symbol of active shadow containment and integration.",
    readTime: "۷ دقیقه",
    readTimeEn: "7 min read",
    category: "تحلیل اساطیر",
    categoryEn: "Mythological Analysis",
    archetype: "shadow",
    mdPath: "/articles/fereydun-shadow-integration.md",
    coverImage: "https://www.razoravan.linkpc.net/images/fereydun-shadow-integration.avif",
    publishDate: "۱۴۰۵/۰۴/۱۴",
    publishDateEn: "July 4, 2026",
    tags: ["فریدون", "سایه", "ضحاک", "یکپارچه‌سازی", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Fereydun", "Shadow", "Zahhak", "Integration", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "12",
    slug: "esfandiar-persona",
    title: "اسفندیار: زندانی پرسونای رویین‌تنی",
    titleEn: "Esfandiyar: Imprisoned Within the Invulnerable Persona",
    brief: "بررسی تراژدی اسفندیار از منظر مفهوم پرسونای یونگ و عواقب غفلت از خودِ واقعی با همسان‌پنداری با نقاب‌های صلب اجتماعی و آسیب‌ناپذیری کاذب.",
    briefEn: "A psychological analysis of Esfandiyar through Jung's concept of the Persona, exploring the tragedy of over-identifying with social masks and false invulnerability.",
    readTime: "۶ دقیقه",
    readTimeEn: "6 min read",
    category: "نقاب اجتماعی",
    categoryEn: "Social Persona",
    archetype: "persona",
    mdPath: "/articles/esfandiar-persona.md",
    coverImage: "https://www.razoravan.linkpc.net/images/esfandiyar-and-persona.avif",
    publishDate: "۱۴۰۵/۰۴/۱۰",
    publishDateEn: "June 30, 2026",
    tags: ["اسفندیار", "نقاب", "رویین‌تنی", "شاهنامه", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Esfandiyar", "Persona", "Invulnerability", "Shahnameh", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "11",
    slug: "bijan-and-repressed-trauma",
    title: "بیژن در چاه افراسیاب: تروما و خاطره‌ی سرکوبشده و زندان درونی",
    titleEn: "Bijan in Afrasiab's Well: Trauma, Repressed Memory, and the Inner Prison",
    brief: "تحلیل روان‌شناختی اسطوره‌ی بیژن در چاه افراسیاب بر اساس نظریه تروما و خاطره‌ی سرکوب‌شده‌ی یونگ و چگونگی رهایی از زندان درون.",
    briefEn: "A profound psychological analysis of Bijan in Afrasiab's well, exploring Jungian repressed trauma, defence mechanisms, and inner liberation.",
    readTime: "۷ دقیقه",
    readTimeEn: "7 min read",
    category: "ناخودآگاه و سایه",
    categoryEn: "Unconscious & Shadow",
    archetype: "shadow",
    mdPath: "/articles/bijan-and-repressed-trauma.md",
    coverImage: "https://www.razoravan.linkpc.net/images/bijan-and-repressed-trauma.avif",
    publishDate: "۱۴۰۵/۰۴/۱۰",
    publishDateEn: "June 30, 2026",
    tags: ["بیژن", "تروما", "سرکوب", "افراسیاب", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Bijan", "Trauma", "Repression", "Afrasiab", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "10",
    slug: "tahmineh-and-mother-archetype",
    title: "تهمینه: کهن‌الگوی مادر و میل ناخودآگاه برای پیوند",
    titleEn: "Tahmineh: The Mother Archetype and the Unconscious Desire for Union",
    brief: "تحلیل عمیق اسطوره تهمینه به عنوان نماد کهن‌الگوی مادر بر اساس نظریات کارل گوستاو یونگ و میل ناخودآگاه او به پیوند و کنشگری مستقل زنانه.",
    briefEn: "A deep analysis of Tahmineh as a symbol of the Mother archetype based on Carl Jung's psychology and her unconscious desire for union and independent female agency.",
    readTime: "۷ دقیقه",
    readTimeEn: "7 min read",
    category: "کهن‌الگوی مادر",
    categoryEn: "Mother Archetype",
    archetype: "mother",
    mdPath: "/articles/tahmineh-and-mother-archetype.md",
    coverImage: "https://www.razoravan.linkpc.net/images/tahmineh-and-mother-archetype.avif",
    publishDate: "۱۴۰۵/۰۴/۱۰",
    publishDateEn: "June 30, 2026",
    tags: ["تهمینه", "مادر", "کهن‌الگوی مادر", "رستم", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Tahmineh", "Mother", "Mother Archetype", "Rostam", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "9",
    slug: "gordafarid-and-heros-journey",
    title: "گردآفرید: سفر قهرمانی زن در شاهنامه",
    titleEn: "Gordafarid: The Female Hero's Journey in the Shahnameh",
    brief: "تحلیل روان‌شناختی اسطوره گردآفرید بر اساس سفر قهرمان جوزف کمبل و فرآیند فردیت یونگ به عنوان نمونه‌ای درخشان از یکپارچگی آنیموس و آنیما.",
    briefEn: "A deep psychological analysis of Gordafarid based on Joseph Campbell's Hero's Journey and Jung's Individuation as a brilliant model of integrated Animus and Anima.",
    readTime: "۶ دقیقه",
    readTimeEn: "6 min read",
    category: "سفر قهرمان",
    categoryEn: "Hero's Journey",
    archetype: "hero",
    mdPath: "/articles/gordafarid-and-heros-journey.md",
    coverImage: "https://www.razoravan.linkpc.net/images/gordafarid-and-heros-journey.avif",
    publishDate: "۱۴۰۵/۰۴/۱۰",
    publishDateEn: "June 30, 2026",
    tags: ["گردآفرید", "قهرمان", "سفر قهرمان", "زن", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Gordafarid", "Hero", "Hero's Journey", "Woman", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "8",
    slug: "farangis-and-animus",
    title: "فرنگیس و آنیموس: کهن‌الگوی مرد درون زن در شاهنامه",
    titleEn: "Farangis and Animus: The Archetype of the Masculine Within the Feminine in the Shahnameh",
    brief: "بررسی روان‌شناختی اسطوره فرنگیس دختر افراسیاب بر پایه مفهوم آنیموس کارل یونگ به عنوان الگوی عقلانیت و کنشگری فعال مستقل زنانه.",
    briefEn: "A deep psychological analysis of Farangis based on Carl Jung's concept of Animus as a model of wisdom and independent female agency.",
    readTime: "۷ دقیقه",
    readTimeEn: "7 min read",
    category: "آنیما و آنیموس",
    categoryEn: "Anima & Animus",
    archetype: "anima_animus",
    mdPath: "/articles/farangis-and-animus.md",
    coverImage: "https://www.razoravan.linkpc.net/images/farangis-and-animus.avif",
    publishDate: "۱۴۰۵/۰۴/۱۰",
    publishDateEn: "June 30, 2026",
    tags: ["فرنگیس", "آنیموس", "زن", "افراسیاب", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Farangis", "Animus", "Woman", "Afrasiab", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "7",
    slug: "rustam-modern-era",
    title: "رستم در عصر مدرن: نقاب قهرمانی و زوال معنا در دنیای امروز",
    titleEn: "Rostam in the Modern Era: The Heroic Persona & The Decline of Meaning in Today's World",
    brief: "تحلیل روانشناختی و کهن‌الگویی زره و نقاب رستم دستان و چالش‌های انسان امروز در مواجهه با نقاب قهرمانی و آسیب‌پذیری‌های روان.",
    briefEn: "A psychological and archetypal analysis of Rostam's armor and persona, and the challenges of modern humans in facing the heroic mask and psychic vulnerabilities.",
    readTime: "۷ دقیقه",
    readTimeEn: "7 min read",
    category: "روان‌کاوی کهن‌الگویی",
    categoryEn: "Archetypal Psychoanalysis",
    archetype: "persona",
    mdPath: "/articles/rustam-modern-era.md",
    coverImage: "https://www.razoravan.linkpc.net/images/rustam-modern-era.avif",
    publishDate: "۱۴۰۵/۰۴/۰۹",
    publishDateEn: "June 29, 2026",
    tags: ["رستم", "نقاب", "قهرمان", "مدرنیته", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Rostam", "Persona", "Hero", "Modernity", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "6",
    slug: "midlife-crisis-jung",
    title: "بحران میانسالی و فراخوان نیمه دوم عمر",
    titleEn: "Midlife Crisis & The Call of the Second Half of Life",
    brief: "بررسی عمیق بحران میانسالی از دیدگاه کارل گوستاو یونگ به عنوان یک فراخوان روانی برای گذار از من (Ego) به خود (Self).",
    briefEn: "A deep exploration of midlife crisis from Carl Jung's perspective as a psychic calling to transition from Ego to Self.",
    readTime: "۶ دقیقه",
    readTimeEn: "6 min read",
    category: "روان‌کاوی کهن‌الگویی",
    categoryEn: "Archetypal Psychoanalysis",
    archetype: "self",
    mdPath: "/articles/midlife-crisis-jung.md",
    coverImage: "https://www.razoravan.linkpc.net/images/midlife-crisis-jung.avif",
    publishDate: "۱۴۰۵/۰۴/۰۷",
    publishDateEn: "June 27, 2026",
    tags: ["میانسالی", "بحران", "تفرد", "نیمه دوم عمر", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Midlife", "Crisis", "Individuation", "Second Half of Life", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "5",
    slug: "father-complex-rostam-sohrab",
    title: "عقده پدر و تراژدی پسرکشی؛ تحلیل روانشناختی رستم و سهراب",
    titleEn: "The Father Complex & Tragedy of Son-Killing: Jungian Analysis of Rostam and Sohrab",
    brief: "بررسی روانشناختی یکی از هولناکترین تراژدیهای شاهنامه بر پایه دیدگاه کارل گوستاو یونگ و مفهوم عقده پدر (Father Complex).",
    briefEn: "A psychological analysis of one of the most horrific tragedies in the Shahnameh based on Carl Gustav Jung's perspectives and the concept of the Father Complex.",
    readTime: "۶ دقیقه",
    readTimeEn: "6 min read",
    category: "روان‌کاوی کهن‌الگویی",
    categoryEn: "Archetypal Psychoanalysis",
    archetype: "shadow",
    mdPath: "/articles/father-complex-rostam-sohrab.md",
    coverImage: "https://www.razoravan.linkpc.net/images/father-complex-rostam-sohrab.avif",
    publishDate: "۱۴۰۵/۰۴/۰۵",
    publishDateEn: "June 25, 2026",
    tags: ["رستم", "سهراب", "عقده پدر", "تراژدی", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Rostam", "Sohrab", "Father Complex", "Tragedy", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "1",
    slug: "zal-and-simurgh",
    title: "افسانه زال و سیمرغ: کهن‌الگوی تفرد و پیر فرزانه",
    titleEn: "The Legend of Zal & Simurgh: The Archetype of Individuation & The Wise Old Man",
    brief: "تحلیلی بر طرد شدن زال به البرزکوه به عنوان نماد هبوط به ناخودآگاه، و سیمرغ به عنوان تجسم تمامیت روان و خرد برتر.",
    briefEn: "An analysis of Zal's exile to Mount Alborz as a symbol of descent into the unconscious, and Simurgh as the embodiment of psychic totality and higher wisdom.",
    readTime: "۸ دقیقه",
    readTimeEn: "8 min read",
    category: "تحلیل اساطیر",
    categoryEn: "Mythological Analysis",
    archetype: "self",
    mdPath: "/articles/zal-and-simurgh.md",
    coverImage: "https://www.razoravan.linkpc.net/images/zal-and-simurgh.avif",
    publishDate: "۱۴۰۵/۰۴/۰۲",
    publishDateEn: "June 22, 2026",
    tags: ["زال", "سیمرغ", "تفرد", "پیر فرزانه", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Zal", "Simurgh", "Individuation", "Wise Old Man", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "2",
    slug: "cyrus-the-great-self",
    title: "کوروش بزرگ و منشور آزادی: تجلی کهن‌الگوی فرمانروای فرزانه",
    titleEn: "Cyrus the Great & The Charter of Freedom: Manifestation of the Wise Ruler Archetype",
    brief: "بررسی روان‌شناختی چگونگی مدیریت اضداد در پادشاهی کوروش هخامنشی بدون فرورفتن در تورم من (Ego Inflation).",
    briefEn: "A psychological examination of managing opposites in Cyrus the Achaemenid's reign without falling into Ego Inflation.",
    readTime: "۶ دقیقه",
    readTimeEn: "6 min read",
    category: "تاریخ و روانشناسی",
    categoryEn: "History & Psychology",
    archetype: "wise_old_man",
    mdPath: "/articles/cyrus-the-great-self.md",
    coverImage: "https://www.razoravan.linkpc.net/images/cyrus-the-great-self.avif",
    publishDate: "۱۴۰۵/۰۳/۱۵",
    publishDateEn: "June 5, 2026",
    tags: ["کوروش", "خودبرتر", "آزادی", "هخامنشی", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Cyrus", "Self", "Freedom", "Achaemenid", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "3",
    slug: "zahhak-and-shadow",
    title: "ضحاک ماردوش و سایه درون",
    titleEn: "Zahhak the Snake-Shouldered & The Inner Shadow",
    brief: "بررسی عمیق روانشناختی اسطوره ضحاک ماردوش بر پایه دیدگاه کارل گوستاو یونگ به عنوان مظهر سایه سرکوب‌شده و فرافکنی شده.",
    briefEn: "A deep psychological analysis of the legend of Zahhak based on Carl Gustav Jung's perspectives on the shadow and projection.",
    readTime: "۷ دقیقه",
    readTimeEn: "7 min read",
    category: "روان‌کاوی کهن‌الگویی",
    categoryEn: "Archetypal Psychoanalysis",
    archetype: "shadow",
    mdPath: "/articles/zahhak-and-shadow.md",
    coverImage: "https://www.razoravan.linkpc.net/images/zahhak-and-shadow.avif",
    publishDate: "۱۴۰۵/۰۴/۰۶",
    publishDateEn: "June 26, 2026",
    tags: ["ضحاک", "سایه", "ماردوش", "فرافکنی", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Zahhak", "Shadow", "Snake-Shouldered", "Projection", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "4",
    slug: "cyrus-shadow-power",
    title: "کوروش بزرگ؛ پادشاهی که سایه قدرت را شکست داد",
    titleEn: "Cyrus the Great: The King Who Conquered the Shadow of Power",
    brief: "بررسی روان‌شناختی چگونگی مهار و کنترل جنبه‌های تاریک روان و فرافکنی سایه در حکومت کوروش بزرگ بر پایه دیدگاه‌های کارل گوستاو یونگ.",
    briefEn: "A psychological exploration of how Cyrus the Great integrated and managed the dark aspects of the psyche and shadow projection from a Jungian perspective.",
    readTime: "۷ دقیقه",
    readTimeEn: "7 min read",
    category: "تاریخ و روانشناسی",
    categoryEn: "History & Psychology",
    archetype: "shadow",
    mdPath: "/articles/cyrus-shadow-power.md",
    coverImage: "https://www.razoravan.linkpc.net/images/cyrus-shadow-power.avif",
    publishDate: "۱۴۰۵/۰۴/۰۵",
    publishDateEn: "June 25, 2026",
    tags: ["کوروش", "سایه", "قدرت", "هخامنشی", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Cyrus", "Shadow", "Power", "Achaemenid", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "16",
    slug: "anahita-divine-anima",
    title: "آناهیتا: آنیمای الهی و بازگشت به بخش فراموش‌شده‌ی روان",
    titleEn: "Anahita: The Divine Anima and Returning to the Forgotten Psyche",
    brief: "بررسی نقش ایزدبانو آناهیتا به عنوان مظهر متعالی و مثبت کهن‌الگوی آنیما در ناخودآگاه انسان و دعوت به احیای بخش‌های شهودی روان.",
    briefEn: "Exploring the goddess Anahita as the supreme positive manifestation of the Anima archetype, calling to restore the intuitive layers of the psyche.",
    readTime: "۷ دقیقه",
    readTimeEn: "7 min read",
    category: "آنیما و آنیموس",
    categoryEn: "Anima & Animus",
    archetype: "anima_animus",
    mdPath: "/articles/anahita-divine-anima.md",
    coverImage: "https://www.razoravan.linkpc.net/images/anahita-divine-anima.avif",
    publishDate: "۱۴۰۵/۰۴/۱۲",
    publishDateEn: "July 2, 2026",
    tags: ["آناهیتا", "آنیما", "زن", "الهی", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Anahita", "Anima", "Woman", "Divine", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "15",
    slug: "jamshid-ego-inflation",
    title: "جمشید: پادشاهی که با تورم «من» تخت و تاج خود را از دست داد",
    titleEn: "Jamshid: The King Who Lost His Throne to Ego Inflation",
    brief: "تحلیل روان‌شناختی ادعای الوهیت جمشید به عنوان مظهر بارز تورم من (Ego Inflation) و پیامدهای آن در فروپاشی فر ایزدی و صعود ضحاک.",
    briefEn: "A psychological analysis of Jamshid claiming divinity as a clear illustration of Ego Inflation and its consequences in losing Divine Grace.",
    readTime: "۷ دقیقه",
    readTimeEn: "7 min read",
    category: "روان‌کاوی کهن‌الگویی",
    categoryEn: "Archetypal Psychoanalysis",
    archetype: "self",
    mdPath: "/articles/jamshid-ego-inflation.md",
    coverImage: "https://www.razoravan.linkpc.net/images/jamshid-ego-inflation.avif",
    publishDate: "۱۴۰۵/۰۴/۱۲",
    publishDateEn: "July 2, 2026",
    tags: ["جمشید", "تورم من", "الوهیت", "ضحاک", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Jamshid", "Ego Inflation", "Divinity", "Zahhak", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "14",
    slug: "kaveh-collective-shadow",
    title: "کاوه‌ی آهنگر و سایه‌ی جمعی: وقتی خشم یک ملت به پرچم تبدیل می‌شود",
    titleEn: "Kaveh the Blacksmith and the Collective Shadow: When a Nation's Grief Becomes a Banner",
    brief: "تحلیل قیام کاوه‌ی آهنگر و درفش کاویانی بر پایه مفهوم سایه‌ی جمعی یونگ و چگونگی مهار خشم انباشته یک ملت تحت استبداد ضحاک.",
    briefEn: "An analysis of Kaveh the Blacksmith's uprising through Jung's Collective Shadow, exploring how repressed grief becomes a national banner.",
    readTime: "۶ دقیقه",
    readTimeEn: "6 min read",
    category: "تحلیل اساطیر",
    categoryEn: "Mythological Analysis",
    archetype: "shadow",
    mdPath: "/articles/kaveh-collective-shadow.md",
    coverImage: "https://www.razoravan.linkpc.net/images/kaveh-collective-shadow.avif",
    publishDate: "۱۴۰۵/۰۴/۱۲",
    publishDateEn: "July 2, 2026",
    tags: ["کاوه", "سایه", "جمعی", "ضحاک", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Kaveh", "Shadow", "Collective", "Zahhak", "Jung", "Archetype", "Analytical Psychology"]
  },
  {
    id: "13",
    slug: "sudabeh-negative-animus",
    title: "سودابه: آنیموس منفی و قدرتی که در خدمت فریب قرار گرفت",
    titleEn: "Sudabeh: The Negative Animus and Power in the Service of Deception",
    brief: "تحلیل رفتار سودابه به عنوان مظهر آنیموس منفی و آنیمای آسیب‌رسان و چگونگی سوق دادن نیروی اراده و هوش به سوی مکر و دسیسه.",
    briefEn: "Analyzing Sudabeh as the embodiment of the negative Animus, showing how iron will and sharp intellect get channeled into deception.",
    readTime: "۷ دقیقه",
    readTimeEn: "7 min read",
    category: "آنیما و آنیموس",
    categoryEn: "Anima & Animus",
    archetype: "anima_animus",
    mdPath: "/articles/sudabeh-negative-animus.md",
    coverImage: "https://www.razoravan.linkpc.net/images/sudabeh-negative-animus.avif",
    publishDate: "۱۴۰۵/۰۴/۱۲",
    publishDateEn: "July 2, 2026",
    tags: ["سودابه", "آنیموس", "فریب", "زن", "یونگ", "کهن\u200cالگو", "روانشناسی تحلیلی"],
    tagsEn: ["Sudabeh", "Animus", "Deception", "Woman", "Jung", "Archetype", "Analytical Psychology"]
  }
];

export { YOUTUBE_VIDEOS } from "./youtube-data";


export const videoConfig = {
  pinnedVideoIds: ["v0"],
  hiddenVideoIds: [] as string[],
  customOrder: [] as string[],
};
